import { useState } from "react";
import { formatNumber, toPersianDigits } from "../utils/format";

/** One day of a user's PAYG usage (mirrors ``AdminUserDailyUsage`` in app/schemas/admin.py). */
export interface AdminUserDailyUsage {
  date: string;
  requests: number;
  tokens: number;
  cost: number;
}

interface AdminUsageSeriesChartProps {
  daily: AdminUserDailyUsage[];
  loading?: boolean;
}

const METRICS: { key: "tokens" | "requests" | "cost"; label: string; unit: string }[] = [
  { key: "tokens", label: "توکن", unit: "توکن" },
  { key: "requests", label: "درخواست", unit: "درخواست" },
  { key: "cost", label: "هزینه", unit: "ریال" },
];

const BAR_HEIGHT = 96;
const ACCENT = "#078783"; // --admin-primary
const HOVER_ACCENT = "#05605D"; // --admin-primary-dark

function dayLabel(iso: string, i: number, total: number): string {
  const d = new Date(iso);
  const day = d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
  if (total <= 10) return day;
  if (i % 5 === 0) return day;
  return "";
}

/**
 * Compact usage-over-time bar chart for the admin user drawer — the admin
 * counterpart of the user-facing KeySpendBarChart in the API-keys tab.
 * Shows one bar per day (last 30 days by default) with a metric toggle
 * (tokens / requests / cost) and a per-day tooltip.
 */
export default function AdminUsageSeriesChart({ daily, loading }: AdminUsageSeriesChartProps) {
  const [metric, setMetric] = useState<"tokens" | "requests" | "cost">("tokens");
  const [hovered, setHovered] = useState<number | null>(null);

  const rows = daily || [];
  const values = rows.map((d) =>
    metric === "tokens"
      ? Number(d.tokens) || 0
      : metric === "requests"
        ? Number(d.requests) || 0
        : Number(d.cost) || 0
  );
  const maxValue = Math.max(1, ...values);
  const hasData = values.some((v) => v > 0);

  if (loading) {
    return (
      <div className="py-6 animate-pulse rounded-xl" style={{ background: "var(--admin-bg-hover)" }} />
    );
  }

  if (!rows.length || !hasData) {
    return (
      <div className="py-6 text-center" style={{ color: "var(--admin-text-tertiary)" }}>
        <p className="text-xs">هنوز مصرفی در این بازه ثبت نشده است</p>
      </div>
    );
  }

  return (
    <div dir="ltr" className="select-none">
      {/* Metric toggle */}
      <div className="flex items-center gap-1.5 mb-3" dir="rtl">
        {METRICS.map((m) => {
          const active = metric === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetric(m.key)}
              className="px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors"
              style={{
                background: active ? ACCENT : "var(--admin-bg-hover)",
                color: active ? "#fff" : "var(--admin-text-secondary)",
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <div className="relative" style={{ height: BAR_HEIGHT }}>
        {/* Horizontal gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <div
            key={f}
            className="absolute left-0 right-0"
            style={{
              top: `${f * 100}%`,
              height: 1,
              background: f === 0 ? "var(--admin-border)" : "var(--admin-border-light)",
            }}
          />
        ))}

        {/* Bars */}
        <div className="absolute inset-0 flex items-end gap-px">
          {rows.map((d, i) => {
            const v = values[i];
            const pct = maxValue > 0 ? (v / maxValue) * 100 : 0;
            const isHovered = hovered === i;
            const showTooltip = isHovered && v > 0;
            return (
              <div
                key={i}
                className="relative flex-1 h-full flex flex-col justify-end"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                {showTooltip && (
                  <div
                    className="absolute bottom-full left-1/2 -translate-x-1/2 z-30 whitespace-nowrap pointer-events-none"
                    style={{ marginBottom: 2 }}
                    dir="rtl"
                  >
                    <div
                      className="rounded-md px-2 py-1 text-[10px]"
                      style={{
                        background: "rgba(26,26,26,0.92)",
                        color: "#fff",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.16)",
                      }}
                    >
                      <span style={{ opacity: 0.75 }}>{new Date(d.date).toLocaleDateString("fa-IR", { day: "numeric", month: "long" })}</span>
                      <span className="mx-1 font-medium">·</span>
                      <span className="font-medium font-number">{toPersianDigits(formatNumber(v))}</span>
                    </div>
                  </div>
                )}
                <div
                  className="w-full rounded-t"
                  style={{
                    height: `${Math.max(pct, v > 0 ? 2 : 0)}%`,
                    background: isHovered ? HOVER_ACCENT : ACCENT,
                    opacity: hovered !== null && !isHovered ? 0.4 : 1,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* X labels */}
      <div className="flex gap-px mt-1" dir="ltr">
        {rows.map((d, i) => (
          <div key={i} className="flex-1 text-center overflow-hidden">
            <span dir="rtl" className="inline-block text-[9px] truncate" style={{ color: "var(--admin-text-tertiary)" }}>
              {dayLabel(d.date, i, rows.length)}
            </span>
          </div>
        ))}
      </div>

      <p className="text-[10px] mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
        {METRICS.find((m) => m.key === metric)?.label} در هر روز — ۳۰ روز اخیر
      </p>
    </div>
  );
}